'use client';
import { PLP_FILTERS_STATE, PLP_RESOURCE_PATH } from '@cookies/Constants/Cookie.constants';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { postcodeAtom } from '@core/Atoms/Postcode/Postcode.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { PAGE_TYPE } from '@core/Constants/PageType';
import { Locale } from '@core/Entities/Locale/Locale.entity';
import { FilterKey, FilterParams } from '@core/Entities/Search/QueryParams.entity';
import { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { Make } from '@core/Entities/Vehicle/Make.entity';
import { Model } from '@core/Entities/Vehicle/Model.entity';
import { getModelNameFromId } from '@core/Services/Search/Utils/GetModelNameFromId';
import { createPathFromFilters } from '@core/Utils/Search/CreatePathFromFilters';
import { Breadcrumbs } from '@gds/Breadcrumbs/Breadcrumbs';
import Button from '@gds/Button/Button';
import { ChevronLeft } from '@gds/Icons/Paths/ChevronLeft';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { generateBreadcrumbClickedTracking } from '@tracking/BreadcrumbClicked.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import { useAtomValue } from 'jotai';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';

import { pageTypeAtom } from 'Atoms/App/PageTypeAtom';

import { safeJsonParse } from 'Utils/Json/SafeJsonParse';

import styles from './PdpBreadcrumb.module.css';

interface PdpBreadcrumbProps {
  strings: Record<string, string>;
  locale: Locale;
  make: Make;
  model: Model;
}

const PdpBreadcrumb: React.FC<PdpBreadcrumbProps> = ({
  strings,
  locale,
  make,
  model,
}: PdpBreadcrumbProps) => {
  const router = useRouter();
  const pageType = useAtomValue(pageTypeAtom);
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const {
    filteredPlpBrdcmbText,
    makeModelPlpBrdcmbText,
    backBrdcmbText,
    homeBrdcmbText,
    allCarsBrdcmbText,
  } = strings;

  const experiments = useAtomValue(experimentsAtom);
  const isBreadcrumbsV3PdpExperiment = experiments.find(
    experiment => experiment.experimentId === 'breadcrumbs-v3-pdp-experiment',
  );
  const decodedValue = decodeURIComponent(Cookies.get(PLP_FILTERS_STATE) ?? '');
  const postcodeValue = useAtomValue(postcodeAtom);
  const filterParams: FilterParams = safeJsonParse(decodedValue, {});
  const filterParamsArray: Filter[] = Object.entries(filterParams).map(([key, value]) => ({
    id: key as FilterKey,
    value: value !== undefined ? (Array.isArray(value) ? value : [value]) : undefined,
  }));

  const plpUrl = createPathFromFilters(filterParamsArray, locale);
  const isFilteredPlp = pageType === PAGE_TYPE.Plp;

  const getBreadcrumbText = () => {
    if (isFilteredPlp) return filteredPlpBrdcmbText;
    return makeModelPlpBrdcmbText;
  };

  const modelSlug = getModelNameFromId(model.id!);

  const handleClick = () => {
    let url = '/autos';
    if (isFilteredPlp) {
      const plpResourcePath = Cookies.get(PLP_RESOURCE_PATH);
      const isDealershipsOrLocationsPlp =
        plpResourcePath?.includes('locations') || plpResourcePath?.includes('dealerships');

      url = isDealershipsOrLocationsPlp
        ? `/${locale}${plpResourcePath}${plpUrl.split('/autos')[1]}`
        : plpUrl;
    } else {
      url = `/autos/make/${make.id}/model/${modelSlug}`;
    }
    const { event, context } = generateBreadcrumbClickedTracking({
      searchParams,
      userId,
      formState,
      ctaWording: getBreadcrumbText(),
      ctaUrl: url,
      clickPosition: 'pdp',
      ctaPosition: 1,
    });

    trackCustomEvent({ event, context });
    router.push(url);
  };

  const { postcode = postcodeValue[locale], lat, lon } = filterParams || {};

  const postcodeSlug = postcode ? `postcode=${postcode}` : null;
  const latSlug = lat ? `&lat=${lat}` : null;
  const lonSlug = lon ? `&lon=${lon}` : null;

  const breadcrumbs = [
    { link: `/${locale}`, title: homeBrdcmbText, hide: true },
    {
      link: `/${locale}/autos/?${[postcodeSlug, latSlug, lonSlug].filter(Boolean).join('&')}`,
      title: allCarsBrdcmbText,
      hide: true,
    },
    {
      link: `/${locale}/autos/make/${make.id}/?${[postcodeSlug, latSlug, lonSlug]
        .filter(Boolean)
        .join('&')}`,
      title: `${make.label}`,
      hide: false,
    },
    {
      link: `/${locale}/autos/make/${make.id}/model/${modelSlug}/?${[postcodeSlug, latSlug, lonSlug]
        .filter(Boolean)
        .join('&')}`,
      title: `${model.label}`,
      hide: false,
    },
  ];

  const {
    breakpoints: { isMobile },
  } = useBreakpoint();

  if (isBreadcrumbsV3PdpExperiment && isBreadcrumbsV3PdpExperiment?.variationId === '1') {
    return (
      <div className={styles.container}>
        {isFilteredPlp ? (
          <Button
            variant="link"
            size="small"
            className={styles.backButton}
            leftIcon={<ChevronLeft />}
            onClick={handleClick}
            data-pdp-breadcrumb-v3
          >
            {isMobile ? '' : backBrdcmbText}
          </Button>
        ) : null}
        <Breadcrumbs
          breadcrumbs={breadcrumbs}
          allowLastItemAsLink
          data-pdp-breadcrumb
          allowToggleHidden={false}
          customShowHide
        />
      </div>
    );
  }

  return (
    <Button
      variant="link"
      className={styles.backButton}
      leftIcon={<ChevronLeft />}
      onClick={handleClick}
      data-pdp-breadcrumb
    >
      {getBreadcrumbText()}
    </Button>
  );
};

export default PdpBreadcrumb;
